import { create } from 'zustand';
import type {
  RunState,
  RoomData,
  HintWithMetadata,
  GuessResult,
  RoomCompletionResult,
  PowerUpItem,
} from '@crackpoint/shared/types';

type GameScreen = 'title' | 'room' | 'roomComplete' | 'gameOver';

interface GuessRecord {
  digits: number[];
  correct_placed: number;
  correct_misplaced: number;
  solved: boolean;
}

interface GameStore {
  run: RunState | null;
  room: RoomData | null;
  screen: GameScreen;
  input: number[];
  attempts: GuessRecord[];
  revealedReserve: HintWithMetadata[];
  usedPowerUps: PowerUpItem[];
  usedPowerUpIds: Set<number>;
  usedHelp: boolean;
  hiddenRevealed: boolean;
  roomStars: number;
  showOptimal: boolean;
  lastGuessResult: GuessResult | null;
  completionResult: RoomCompletionResult | null;
  solution: number[] | null;

  setRun: (run: RunState | null) => void;
  setRoom: (room: RoomData | null) => void;
  setScreen: (screen: GameScreen) => void;
  addDigit: (digit: number) => void;
  removeDigit: () => void;
  clearInput: () => void;
  recordGuess: (result: GuessResult, guess: number[]) => void;
  addReserveHint: (hint: HintWithMetadata) => void;
  addUsedPowerUp: (index: number, powerUp: PowerUpItem) => void;
  setCompletionResult: (result: RoomCompletionResult) => void;
  setSolution: (solution: number[] | null) => void;
  setShowOptimal: (show: boolean) => void;
  resetRoomState: () => void;
  resetGame: () => void;
}

export const useGameStore = create<GameStore>()((set) => ({
  run: null,
  room: null,
  screen: 'title',
  input: [],
  attempts: [],
  revealedReserve: [],
  usedPowerUps: [],
  usedPowerUpIds: new Set(),
  usedHelp: false,
  hiddenRevealed: false,
  roomStars: 0,
  showOptimal: false,
  lastGuessResult: null,
  completionResult: null,
  solution: null,

  setRun: (run) => set({ run }),

  setRoom: (room) =>
    set({
      room,
      input: [],
      attempts: [],
      revealedReserve: [],
      usedPowerUps: [],
      usedPowerUpIds: new Set(),
      usedHelp: false,
      hiddenRevealed: false,
      roomStars: 0,
      showOptimal: false,
      lastGuessResult: null,
      completionResult: null,
      solution: null,
    }),

  setScreen: (screen) => set({ screen }),

  addDigit: (digit) =>
    set((state) => {
      if (!state.room) return state;
      if (state.input.length >= state.room.code_length) return state;
      return { input: [...state.input, digit] };
    }),

  removeDigit: () =>
    set((state) => ({
      input: state.input.slice(0, -1),
    })),

  clearInput: () => set({ input: [] }),

  recordGuess: (result, guess) =>
    set((state) => {
      const attempt: GuessRecord = {
        digits: guess,
        correct_placed: result.correct_placed,
        correct_misplaced: result.correct_misplaced,
        solved: result.solved,
      };

      const updates: Partial<GameStore> = {
        attempts: [...state.attempts, attempt],
        lastGuessResult: result,
        input: [],
      };

      if (result.hidden_hint_revealed) {
        updates.hiddenRevealed = true;
      }

      if (result.run_ended && state.run) {
        updates.run = { ...state.run, lives_remaining: result.lives_remaining, state: 'completed' };
      } else if (state.run) {
        updates.run = { ...state.run, lives_remaining: result.lives_remaining };
      }

      return updates;
    }),

  addReserveHint: (hint) =>
    set((state) => ({
      revealedReserve: [...state.revealedReserve, hint],
      usedHelp: true,
    })),

  addUsedPowerUp: (index, powerUp) =>
    set((state) => ({
      usedPowerUps: [...state.usedPowerUps, powerUp],
      usedPowerUpIds: new Set([...state.usedPowerUpIds, index]),
      usedHelp: true,
    })),

  setCompletionResult: (result) =>
    set({ completionResult: result, run: result.run_state }),

  setSolution: (solution) => set({ solution }),

  setShowOptimal: (show) => set({ showOptimal: show }),

  resetRoomState: () =>
    set({
      input: [],
      attempts: [],
      revealedReserve: [],
      usedPowerUps: [],
      usedPowerUpIds: new Set(),
      usedHelp: false,
      hiddenRevealed: false,
      roomStars: 0,
      showOptimal: false,
      lastGuessResult: null,
      completionResult: null,
      solution: null,
    }),

  resetGame: () =>
    set({
      run: null,
      room: null,
      screen: 'title',
      input: [],
      attempts: [],
      revealedReserve: [],
      usedPowerUps: [],
      usedPowerUpIds: new Set(),
      usedHelp: false,
      hiddenRevealed: false,
      roomStars: 0,
      showOptimal: false,
      lastGuessResult: null,
      completionResult: null,
      solution: null,
    }),
}));

export const selectCanSubmitGuess = (state: GameStore): boolean => {
  if (!state.room) return false;
  return state.input.length === state.room.code_length;
};

export const selectVisibleHints = (state: GameStore): HintWithMetadata[] => {
  if (!state.room) return [];

  const baseHints = state.room.base_hints.filter((_hint: HintWithMetadata, index: number) => {
    if (state.room?.hidden_hint_index === index && !state.hiddenRevealed) {
      return false;
    }
    return true;
  });

  return [...baseHints, ...state.revealedReserve];
};

export const selectReserveHintsRemaining = (state: GameStore): number => {
  if (!state.room) return 0;
  return state.room.reserve_hint_count - state.revealedReserve.length;
};
